import { Box } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  SprengnetterValuationHubValuationReportModelResponseAvmPriceResponse as AvmPriceResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsaWebResponse,
  ValuationHubCoreModelRealEstateAddress,
} from "@/api/generatedApi";
import { ReportEditIcon } from "@/assets";
import { PanelHeader } from "@/components/panelHeader/PanelHeader";
import type { ReportApi, ReportFormat } from "@/components/reportApi";
import { ValuationReportConfiguration } from "@/components/valuationReportConfiguration/ValuationReportConfiguration";
import { ReportCreatedModal } from "@/components/valuationReportGenerationPanel/ReportCreatedModal";
import "@/components/valuationReportGenerationPanel/translations";
import { colors } from "@/theme/tokens/colors";
import { PropertyInformation } from "@/types/refineRealEstate";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  panel: {
    display: "grid",
    gap: theme.analysisLayout.panelGap,
    backgroundColor: "background.paper",
    padding: theme.analysisLayout.panelPadding,
  },
});

export interface ReportConfigurationPanelProps {
  address: ValuationHubCoreModelRealEstateAddress | null;
  propertyInformation: PropertyInformation | null;
  reportAskingPrice?: number | null;
  onAskingPriceChange?: (price: number | null) => void;
  isAskingPriceEditable?: boolean;
  beforeCreateReport?: () => Promise<void>;
  prosaWebPrice: ProsaWebResponse | null;
  avmPrice: AvmPriceResponse | null;
  refetchStatus: () => unknown | Promise<unknown>;
  reportApi: ReportApi;
  formId?: string;
  onGenerateReportLoadingChange?: (isLoading: boolean) => void;
  isCreateReportModalOpen?: boolean;
  setIsCreateReportModalOpen?: (isOpen: boolean) => void;
  createdReportUrl?: string | null;
  setCreatedReportUrl?: (url: string | null) => void;
  createdReportFileName?: string | null;
  setCreatedReportFileName?: (fileName: string | null) => void;
  sendImageUrls?: boolean;
  reportFormat?: ReportFormat;
  getReportFormat?: () => ReportFormat;
  onReportGenerated?: (format: ReportFormat) => void;
}

export const ReportConfigurationPanel = ({
  address,
  propertyInformation,
  reportAskingPrice,
  onAskingPriceChange,
  isAskingPriceEditable = false,
  beforeCreateReport,
  avmPrice,
  prosaWebPrice,
  refetchStatus,
  reportApi,
  formId,
  onGenerateReportLoadingChange,
  isCreateReportModalOpen: isCreateReportModalOpenProp,
  setIsCreateReportModalOpen: setIsCreateReportModalOpenProp,
  createdReportUrl: createdReportUrlProp,
  setCreatedReportUrl: setCreatedReportUrlProp,
  createdReportFileName: createdReportFileNameProp,
  setCreatedReportFileName: setCreatedReportFileNameProp,
  sendImageUrls = false,
  reportFormat,
  getReportFormat,
  onReportGenerated,
}: ReportConfigurationPanelProps): JSX.Element => {
  const { t } = useTranslation("ValuationReportGenerationPanel");
  const theme = useTheme();
  const styles = createStyles(theme);
  const [internalIsCreateReportModalOpen, setInternalIsCreateReportModalOpen] = useState(false);
  const [internalCreatedReportUrl, setInternalCreatedReportUrl] = useState<string | null>(null);
  const [internalCreatedReportFileName, setInternalCreatedReportFileName] = useState<string | null>(null);

  const isCreateReportModalOpen = isCreateReportModalOpenProp ?? internalIsCreateReportModalOpen;
  const setIsCreateReportModalOpen = setIsCreateReportModalOpenProp ?? setInternalIsCreateReportModalOpen;
  const createdReportUrl = createdReportUrlProp ?? internalCreatedReportUrl;
  const setCreatedReportUrl = setCreatedReportUrlProp ?? setInternalCreatedReportUrl;
  const createdReportFileName = createdReportFileNameProp ?? internalCreatedReportFileName;
  const setCreatedReportFileName = setCreatedReportFileNameProp ?? setInternalCreatedReportFileName;

  const handleDownloadReport = (): void => {
    if (!createdReportUrl) return;

    const a = document.createElement("a");
    a.href = createdReportUrl;
    a.download = createdReportFileName || "valuation-report.pdf";
    a.click();
  };

  const handleOpenReport = (): void => {
    if (!createdReportUrl) return;

    const reportWindow = window.open(createdReportUrl, "_blank", "noopener,noreferrer");
    if (!reportWindow) {
      console.error("Failed to open generated report in a new tab");
    } else {
      reportWindow.focus();
    }
  };

  return (
    <>
      <Box sx={styles.panel}>
        <PanelHeader
          title={t("reportPanel.configuration.mainLabel")}
          icon={
            <ReportEditIcon sx={{ fontSize: theme.analysisLayout.headerIconSize, color: colors.brand.brownDark }} />
          }
          tooltip={{ content: t("reportPanel.description") }}
        />

        <ValuationReportConfiguration
          address={address}
          propertyInformation={propertyInformation}
          reportAskingPrice={reportAskingPrice}
          onAskingPriceChange={onAskingPriceChange}
          isAskingPriceEditable={isAskingPriceEditable}
          beforeCreateReport={beforeCreateReport}
          avmPrice={avmPrice}
          prosaWebPrice={prosaWebPrice}
          setIsReportConfigurationOpen={() => undefined}
          setIsReportCreatedModalOpen={setIsCreateReportModalOpen}
          refetchStatus={refetchStatus}
          reportApi={reportApi}
          setCreatedReportUrl={setCreatedReportUrl}
          setCreatedReportFileName={setCreatedReportFileName}
          formId={formId}
          onGenerateReportLoadingChange={onGenerateReportLoadingChange}
          sendImageUrls={sendImageUrls}
          reportFormat={reportFormat}
          getReportFormat={getReportFormat}
          onReportGenerated={onReportGenerated}
        />
      </Box>
      <ReportCreatedModal
        open={isCreateReportModalOpen}
        setOpen={setIsCreateReportModalOpen}
        onDownloadReport={handleDownloadReport}
        onOpenReport={handleOpenReport}
        canOpenReport={createdReportFileName?.toLowerCase().endsWith(".pdf") ?? true}
      />
    </>
  );
};
